import {
  fileExtension,
  STUDIO_CONTENT_IMAGE_ALLOWED_EXTENSIONS,
  STUDIO_CONTENT_IMAGE_MAX_BYTES,
  STUDIO_CONTENT_IMAGE_PREFERRED_EXTENSIONS,
  STUDIO_CONTENT_IMAGE_SIZE_GREEN_MAX_BYTES,
  STUDIO_CONTENT_IMAGE_SIZE_YELLOW_MAX_BYTES,
} from "@/utils/studioBlobPath";

export type StudioImageSizeBand = "green" | "yellow" | "red";

export type StudioImageFormatStatus =
  | "preferred"
  | "allowed_not_recommended"
  | "unsupported";

export type StudioImageInspection = {
  filename: string;
  extension: string;
  sizeBytes: number;
  sizeBand: StudioImageSizeBand;
  formatStatus: StudioImageFormatStatus;
  /** Hard block — file cannot be uploaded. */
  canUpload: boolean;
  messages: string[];
};

function sizeBandForBytes(sizeBytes: number): StudioImageSizeBand {
  if (sizeBytes < STUDIO_CONTENT_IMAGE_SIZE_GREEN_MAX_BYTES) {
    return "green";
  }
  if (sizeBytes < STUDIO_CONTENT_IMAGE_SIZE_YELLOW_MAX_BYTES) {
    return "yellow";
  }
  return "red";
}

function formatStatusForExtension(ext: string): StudioImageFormatStatus {
  if (!ext || !STUDIO_CONTENT_IMAGE_ALLOWED_EXTENSIONS.has(ext)) {
    return "unsupported";
  }
  if (STUDIO_CONTENT_IMAGE_PREFERRED_EXTENSIONS.has(ext)) {
    return "preferred";
  }
  return "allowed_not_recommended";
}

function formatSizeLabel(sizeBytes: number): string {
  if (sizeBytes < 1024) {
    return `${sizeBytes} B`;
  }
  if (sizeBytes < 1024 * 1024) {
    return `${Math.round(sizeBytes / 1024)} KB`;
  }
  return `${(sizeBytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Inspect a candidate content image before upload.
 * JPEG/JPG are allowed but not recommended; PNG and WebP are preferred.
 * Size bands: green &lt;200KB, yellow &lt;600KB, red otherwise.
 */
export function inspectStudioImageFile(input: {
  filename: string;
  sizeBytes: number;
}): StudioImageInspection {
  const extension = fileExtension(input.filename);
  const formatStatus = formatStatusForExtension(extension);
  const sizeBand = sizeBandForBytes(input.sizeBytes);
  const messages: string[] = [];

  if (formatStatus === "unsupported") {
    messages.push("Unsupported format. Use PNG, WebP, or JPEG/JPG.");
  } else if (formatStatus === "allowed_not_recommended") {
    messages.push(
      "JPEG/JPG is allowed but not recommended. Prefer PNG or WebP.",
    );
  } else {
    messages.push("Preferred format (PNG or WebP).");
  }

  if (input.sizeBytes > STUDIO_CONTENT_IMAGE_MAX_BYTES) {
    messages.push(
      `File is too large (${formatSizeLabel(input.sizeBytes)}). Maximum is 5 MB.`,
    );
  } else if (sizeBand === "green") {
    messages.push(
      `Size looks good (${formatSizeLabel(input.sizeBytes)}; under 200 KB).`,
    );
  } else if (sizeBand === "yellow") {
    messages.push(
      `Size is acceptable but large (${formatSizeLabel(input.sizeBytes)}; under 600 KB).`,
    );
  } else {
    messages.push(
      `Size is large (${formatSizeLabel(input.sizeBytes)}; 600 KB or more). Consider compressing.`,
    );
  }

  const canUpload =
    formatStatus !== "unsupported" &&
    input.sizeBytes <= STUDIO_CONTENT_IMAGE_MAX_BYTES;

  return {
    filename: input.filename,
    extension,
    sizeBytes: input.sizeBytes,
    sizeBand,
    formatStatus,
    canUpload,
    messages,
  };
}
